import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';

import DiscardIcon from 'assets/DiscardIcon';
import ReportProblemIcon from 'assets/report_problem.svg?react';
import SpinnerModal from 'components/SpinnerModal';
import showConfirmModal from 'components/ConfirmModal';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import useUnsavedChangesGuard from 'hooks/useUnsavedChangesGuard';
import { paths } from 'routes/paths';

import ProgressStepper, {
  ProgressStepperStep
} from 'components/RecordsListView/ProgressStepper';

import {
  InvoiceFormValues,
  initialDetailsValues,
  initialProductsValues
} from '../Constants/invoiceFormikData';
import useInvoiceMutations from '../hooks/useInvoiceMutations';
import useInvoiceEvents from '../hooks/useInvoiceEvents';
import invoiceNotification, {
  dismissInvoiceNotification
} from '../components/Notification';
import listPriceNotification from '../helpers/listPriceNotification';
import { printInvoiceViaBridge } from '../components/Notification/invoicePrintBridge';

import DetailsStep from './Steps/DetailsStep';
import ProductsStep from './Steps/ProductsStep';

export type InvoiceMode = 'create' | 'edit' | 'duplicate';

interface InvoiceStepsLayoutProps {
  mode: InvoiceMode;
  invoiceId?: string;
  onSuccess?: () => void;
}

const STEPPER_NAMES: Record<InvoiceMode, string> = {
  create: 'Create Invoice',
  edit: 'Edit Invoice',
  duplicate: 'Create Invoice'
};

const SAVING_MESSAGES: Record<InvoiceMode, string> = {
  create: 'Saving Invoice…',
  edit: 'Saving Invoice…',
  duplicate: 'Saving Invoice…'
};

const INVOICE_CANCEL_BUTTON_PROPS = {
  style: {
    borderRadius: 4,
    background: '#FFF',
    color: '#14151C',
    fontSize: 14,
    fontWeight: 400,
    padding: '9px 16px',
    border: '1px solid #C2C7D0',
    height: 40,
    boxSizing: 'border-box' as const
  }
};

// Single source of truth for the step names, indexed the same as the `steps`
// array below — shared so `handleSave`'s Amplitude "which step did this save
const STEP_NAMES = ['Invoice Details', 'Select Products'];

const InvoiceStepsLayout: React.FC<InvoiceStepsLayoutProps> = ({
  mode,
  invoiceId,
  onSuccess
}) => {
  const history = useHistory();
  useEffect(() => {
    dismissInvoiceNotification();
  }, []);
  const { values, dirty } = useFormikContext<InvoiceFormValues>();
  const {
    saving,
    createInvoice,
    updateInvoice,
    saveListPrices,
    getLastError
  } = useInvoiceMutations();
  const {
    sendSaveInvoiceEvent,
    sendSaveInvoiceEditsEvent,
    sendInitiateDuplicateEvent,
    sendDownloadPdfEvent
  } = useInvoiceEvents();

  const [currentStep, setCurrentStep] = useState(0);
  // `createInvoice`/`updateInvoice` release their own saving flag as soon as the
  // invoice request settles, but the list-price write runs after that. Without a
  // second flag the stepper, the save button and the unsaved-changes guard all
  // go live again while it is still in flight.
  const [savingListPrices, setSavingListPrices] = useState(false);
  const isSaving = saving || savingListPrices;

  const showDiscardModal = (onConfirm: () => void) => {
    if (mode === 'edit') {
      showConfirmModal({
        icon: <ReportProblemIcon />,
        title: 'Discard changes to your record?',
        titleStyles: { fontSize: 16, lineHeight: '24px' },
        titleIconGap: 16,
        content: <>None of the changes to this record will be saved.</>,
        contentStyles: { paddingRight: 0, fontSize: 14, lineHeight: '20px' },
        contentTextStyles: { lineHeight: '20px' },
        contentGutterWidth: 40,
        okText: 'Discard changes',
        okButtonProps: {
          style: {
            borderRadius: 4,
            background: 'var(--Green-green-60, #14803C)',
            padding: '10px 16px',
            color: '#FFF',
            fontSize: 14,
            fontWeight: 400,
            marginLeft: 8
          }
        },
        cancelText: 'Continue editing',
        cancelButtonProps: INVOICE_CANCEL_BUTTON_PROPS,
        onOk: onConfirm
      });
      return;
    }

    showConfirmModal({
      icon: <DiscardIcon />,
      title: 'Discard Invoice?',
      titleStyles: { fontSize: 16, lineHeight: '24px' },
      titleIconGap: 16,
      content: (
        <>
          By leaving the page, you will lose{' '}
          <span className="font-semibold">all data</span> from this record.
        </>
      ),
      contentStyles: { paddingRight: 0, fontSize: 14, lineHeight: '20px' },
      contentTextStyles: { lineHeight: '20px' },
      contentGutterWidth: 40,
      okText: 'Discard record',
      cancelText: 'Continue editing',
      cancelButtonProps: INVOICE_CANCEL_BUTTON_PROPS,
      onOk: onConfirm
    });
  };

  const { allowNavigation } = useUnsavedChangesGuard({
    isDirty: dirty,
    isSaving,
    showDiscardModal
  });

  const navigateToInvoices = () => {
    allowNavigation();
    history.push(paths.invoices);
  };

  // Shared by both the error and success paths below — the only difference
  // between them is the result label and whether an error is available.
  const sendSaveResultEvent = (
    saveSucceeded: boolean,
    apiCallLengthMs: number,
    stepName: string,
    numberOfProducts: number,
    lastError?: unknown
  ) => {
    const result = saveSucceeded ? 'Success' : 'Error';
    if (mode === 'edit') {
      sendSaveInvoiceEditsEvent(result, apiCallLengthMs, lastError, stepName);
      return;
    }
    sendSaveInvoiceEvent(
      result,
      apiCallLengthMs,
      numberOfProducts,
      mode === 'duplicate',
      lastError
    );
  };

  const showSaveSuccessNotification = (newId?: string) => {
    if (mode === 'edit') {
      invoiceNotification('EDIT_SUCCESS');
      return;
    }
    invoiceNotification('CREATE_SUCCESS', undefined, {
      onDuplicate: newId
        ? () => {
            sendInitiateDuplicateEvent('Confirmation toast');
            history.push(`${paths.invoices}/${newId}/duplicate`);
          }
        : undefined,
      onPrint: newId
        ? () => {
            sendDownloadPdfEvent('Confirmation toast');
            printInvoiceViaBridge({
              invoiceId: newId,
              title: values.invoiceNumber,
              vendor: values.vendor
            });
          }
        : undefined
    });
  };

  const handleSave = async () => {
    const stepName = STEP_NAMES[currentStep];
    const startedAt = performance.now();
    const result: any =
      mode === 'edit' && invoiceId
        ? await updateInvoice(invoiceId, values)
        : await createInvoice(values);
    const apiCallLengthMs = Math.round(performance.now() - startedAt);
    const numberOfProducts = values.invoiceProducts.filter(product =>
      Boolean(product.productId)
    ).length;

    // createInvoice/updateInvoice resolve to the axios response on success and
    // to `undefined` after swallowing an error into their `error` state.
    if (result === undefined) {
      // FR-096: "Unable to save Invoice." error banner with Contact Support.
      invoiceNotification('CREATE_OR_EDIT_ERROR');
      sendSaveResultEvent(
        false,
        apiCallLengthMs,
        stepName,
        numberOfProducts,
        getLastError()
      );
      return;
    }

    sendSaveResultEvent(true, apiCallLengthMs, stepName, numberOfProducts);

    // Only now may a list price change: the user's intent has ridden on the
    // line items until this point, and abandoning the invoice leaves every
    // product's price untouched. A failure here cannot unwind the invoice that
    // has already been saved, so it is reported on its own rather than turning
    // a successful save into an error.
    setSavingListPrices(true);
    let listPricesSaved: boolean;
    try {
      listPricesSaved = await saveListPrices(values);
    } finally {
      setSavingListPrices(false);
    }
    if (!listPricesSaved) {
      const markedProducts = values.invoiceProducts.filter(
        p => p.saveAsNewListPrice && Boolean(p.productId)
      );
      listPriceNotification({
        count: markedProducts.length,
        productName:
          markedProducts.length === 1
            ? markedProducts[0]?.productName
            : undefined
      });
    }

    if (onSuccess) {
      onSuccess();
    }

    showSaveSuccessNotification(result?.data?.id);
    navigateToInvoices();
  };

  const handleCancel = () => {
    if (!dirty) {
      navigateToInvoices();
      return;
    }

    showDiscardModal(navigateToInvoices);
  };

  const handleStepChange = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const saveButtonContent = (
    <span className="w-full text-center">Save Invoice</span>
  );

  const steps: ProgressStepperStep[] = [
    {
      name: STEP_NAMES[0],
      component: DetailsStep,
      initialValues: initialDetailsValues,
      prerequisites: [] as (keyof InvoiceFormValues)[],
      nextButtonLabel: 'Select Products',
      // In edit mode the whole invoice (details + products) is already loaded
      // into Formik before the stepper renders, so the user can save from the
      // Details step without stepping through Products — matching Applications'
      // edit flow. The save button is still gated by full-form validity.
      ...(mode === 'edit'
        ? { showButton: true, handleSave, saveButtonContent }
        : {})
    },
    {
      name: STEP_NAMES[1],
      component: ProductsStep,
      initialValues: initialProductsValues,
      // Products isn't reachable until the required Details fields are
      // valid (FR-065) — gate on Details' own required fields, not on
      // invoiceProducts itself.
      prerequisites: ['invoiceNumber', 'invoiceDate'],
      backButtonLabel: 'Invoice Details',
      showButton: true,
      handleSave,
      saveButtonContent
    }
  ];

  return (
    <div className="h-full w-full relative">
      <Breadcrumb
        hideOrganizationDropdown={false}
        hideCropSeasonDropdown={false}
        disabledCropSeasonCreate
        allowMultipleCropSeasonSelection
        disabled
      >
        <Breadcrumb.Item title="Invoices" value={STEPPER_NAMES[mode]} isLast />
      </Breadcrumb>
      <ProgressStepper
        stepperName={STEPPER_NAMES[mode]}
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        disableSaveButton={isSaving}
        disableStepButtons={isSaving}
        onCancel={handleCancel}
        titleColSpan={12}
        stepsColSpan={12}
      />
      <SpinnerModal open={isSaving} message={SAVING_MESSAGES[mode]} />
    </div>
  );
};

export default InvoiceStepsLayout;
